// Angular imports
import { Component, OnInit, Input, ViewChild, ElementRef, ChangeDetectionStrategy, HostBinding } from '@angular/core';
import { PercentPipe } from '@angular/common';

// Cell base class
import { CellComponent } from '../cell.component';

// Import utils
import PercentUtils from '../../../utils/percent';
import NumberUtils from '../../../utils/number';

export type InputCellType = 'number' | 'percent' | 'dimensions' | 'text';

@Component({
    selector: 'input-cell',
    templateUrl: './input-cell.component.html',
    styleUrls: ['./input-cell.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [PercentPipe],
    standalone: false
})
export class InputCellComponent extends CellComponent<number | string> implements OnInit {
    // The type for this input cell
    @Input() public type: InputCellType = 'number';

    // Whether or not the value of this cell may be null
    @Input() public nullable: boolean = true;

    // The minimum value of this input (only functions if type is number or percent)
    @Input() public min?: number;

    // Background color
    @Input() @HostBinding('style.background') public color?: string;

    // Reference to the input element
    @ViewChild('input') _inputElement!: ElementRef;

    public ngOnInit() {
        super.ngOnInit();
    }

    /**
     * Place the cursor on this cell's content
     * @param shallow Whether or not to focus shallowly
     * @returns void
     */
    public focus(shallow: boolean = true): void {
        // Set whether we have shallow focus or not
        if (this.hasShallowFocus === null)
            this.hasShallowFocus = shallow;

        // Focus the input
        this._inputElement.nativeElement.focus();

        // Select all content
        if (this.selectOnFocus)
            this.selectContent();

        // Trigger focus event
        this.onFocus.emit();
    }

    /**
     * Remove focus from this cell's content. This does not remove the selection
     * @returns void
     */
    public blur(): void {
        // Blur the input
        this._inputElement.nativeElement.blur();

        // Trigger blur event
        this.onBlur.emit();
    }

    /**
     * Select the cell.
     * @returns void
     */
    public select(setOrigin: boolean = false): void {
        // If already selected, return
        if (this.isSelected) return;

        // Emit selection changed event
        this.selectionChange.emit({
            origin: setOrigin ? this.selection.origin ?? this.selection.current : undefined,
            current: { row: this.rowIndex, column: this.column.index } 
        });
    }

    /**
     * Select all of the cell's content.
     * @returns void
     */
    public selectContent(): void {
        // Select the cell's content
        this._inputElement.nativeElement.select();
    }

    /* --- Event handlers --- */

    // Handle model change event
    public async _handleModelChange(value: string) {
        let unformattedValue: number | string | undefined;

        // Unformat the value if required
        if (this.type === 'percent')
            unformattedValue = PercentUtils.unformat(value, this.min);
        else if (this.type === 'number')
            unformattedValue = NumberUtils.unformat(value, this.min);
        else
            unformattedValue = value;

        // Store old value
        const oldValue = this.value;

        // If the value is the same, clear the current value
        if (unformattedValue === this.value)
            await this.clearValue();

        // If the value is not valid, clear and assign the old value
        if (unformattedValue == undefined && (value.length > 0 || !this.nullable)) {
            await this.clearValue();
            unformattedValue = oldValue;
        }

        // Assign the value
        this.value = unformattedValue ?? undefined;

        // Emit change event
        this.valueChange.emit(this.value);
    }

    // Handle click event
    public _handleClick(event: MouseEvent) {
        // Check if this cell is currently not selected
        if (!this.isSelected) {
            // Prevent default
            event.preventDefault();

            // Focus and then blur (to lose other focus)
            this._inputElement.nativeElement.focus();

            // Blur the content
            this.blur();

            // Select the cell
            return this.select(event.shiftKey);
        }

        // The cell is selected, deep focus
        this.focus(false);
    }

    // Handle focus event
    public _handleFocus(event: FocusEvent) {
        // Stop propagation
        event.preventDefault();
        event.stopPropagation();
    }

    // Handle blur event
    public _handleBlur(event: Event) {
        // Clear has shallow focus
        this.hasShallowFocus = null;
    }

    // Handle tabs, enters and arrow keys
    public _handleKeydown(event: KeyboardEvent) {
        if (event.key !== 'Enter' && event.key !== 'Tab' && !(event.key === 'ArrowLeft' && this.hasShallowFocus) && !(event.key === 'ArrowRight' && this.hasShallowFocus) && event.key !== 'ArrowUp' && event.key !== 'ArrowDown')
            return event.stopPropagation();

        // Prevent default behavior
        event.preventDefault();
        event.stopPropagation();
        // console.log(new Date().valueOf())

        // console.log(event.key);
        if ((event.key === 'ArrowRight' && this.hasShallowFocus) || event.key === 'Enter' || (event.key === 'Tab' && !event.shiftKey))
            this.selectRight.emit();
        if ((event.key === 'ArrowLeft' && this.hasShallowFocus) || (event.key === 'Tab' && event.shiftKey))
            this.selectLeft.emit();
        if (event.key === 'ArrowUp')
            this.selectAbove.emit();
        if (event.key === 'ArrowDown')
            this.selectBelow.emit();

        // Remove focus here
        this.blur();
    }

    public _purgeEvent(event: Event) {
        event.stopPropagation();
    }

    /* --- Helpers --- */

    private async clearValue() {
        this.value = this.value === null ? 0 : null;
        return new Promise(resolve => {
            setTimeout(resolve, 0);
        });
    }
}
